import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  Package, Wrench, CheckCircle, ClipboardList,
  Inbox, X, HelpCircle, Play, LogOut, ScanBarcode, Check, Truck, Camera, Send, MapPin, Store, Image, Search
} from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';
import { API_URL } from '../config/api';
import useEscapeClose from '../hooks/useEscapeClose';
import './Workshop.css';

// Arama için Türkçe-duyarsız normalleştirme: büyük/küçük harf ve Türkçe karakter farkını
// yok sayar ("ALI", "Ali", "alı", "ALİ" hepsi "ali" olur; "Şükrü" → "sukru").
// Sadece toLocaleLowerCase('tr') yetmiyordu: veride "I" ile yazılmış "ALI" → "alı" olup
// "ali" aramasıyla eşleşmiyordu.
const normalizeSearch = (value) => String(value ?? '')
  .toLocaleLowerCase('tr')
  .replace(/ı/g, 'i').replace(/ş/g, 's').replace(/ğ/g, 'g')
  .replace(/ü/g, 'u').replace(/ö/g, 'o').replace(/ç/g, 'c')
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .replace(/\s+/g, ' ')
  .trim();

function Workshop({ user, onLogout, selectedStore }) {
  const [activeTab, setActiveTab] = useState('atWorkshop');
  const [repairs, setRepairs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStoreId, setFilterStoreId] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRepair, setSelectedRepair] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [workshopNote, setWorkshopNote] = useState('');
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef(null);

  // Ürün teslim alma için state'ler
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [selectedForReceive, setSelectedForReceive] = useState([]);
  const [barcodeInput, setBarcodeInput] = useState('');
  // Aynı barkodlu modal iki iş görür: 'receive' (Yolda → Atölyede teslim al) ve
  // 'dispatch' (atölyedeki ürünün mağazaya çıkışı → Mağazaya Yolda).
  const [scanMode, setScanMode] = useState('receive');
  const [scanSubmitting, setScanSubmitting] = useState(false);
  const barcodeInputRef = useRef(null);

  // Kamera barkod tarama için state'ler
  const [isCameraMode, setIsCameraMode] = useState(false); // Varsayılan manuel giriş
  const [isCameraActive, setIsCameraActive] = useState(false);
  const html5QrCodeRef = useRef(null);
  const scannerContainerRef = useRef(null);

  // Çıkış yap modal için state'ler
  const [showDispatchModal, setShowDispatchModal] = useState(false);
  const [dispatchRepair, setDispatchRepair] = useState(null);
  // Mağazaya çıkışta her ürün için zorunlu atölye notu: { [repairId]: not }
  const [dispatchNotes, setDispatchNotes] = useState({});
  const [stores, setStores] = useState([]);

  // Fotoğraf yükleme ve büyütme
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [viewingPhoto, setViewingPhoto] = useState(null);

  const handleWorkshopPhotoUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !selectedRepair) return;
    setUploadingPhoto(true);
    try {
      const uploadFormData = new FormData();
      for (let i = 0; i < files.length; i++) {
        uploadFormData.append('photos', files[i]);
      }
      const uploadRes = await fetch(`${API_URL}/repairs/upload-photos`, { method: 'POST', body: uploadFormData });
      const uploadData = await uploadRes.json();
      if (uploadData.success && uploadData.photos) {
        // Önce DB'den güncel veriyi çek
        const freshRes = await fetch(`${API_URL}/repairs/${selectedRepair.id}`);
        const freshRepair = await freshRes.json();

        // Mevcut photos'u al
        let existingPhotos = [];
        try { existingPhotos = freshRepair.photos ? (typeof freshRepair.photos === 'string' ? JSON.parse(freshRepair.photos) : freshRepair.photos) : []; } catch (err) { existingPhotos = []; }
        const allPhotos = [...existingPhotos, ...uploadData.photos];

        // items'ı al ve ilk ürünün fotoğraflarına ekle
        let items = [];
        try { items = freshRepair.items ? (typeof freshRepair.items === 'string' ? JSON.parse(freshRepair.items) : freshRepair.items) : []; } catch (err) { items = []; }
        // Deep copy yap
        items = JSON.parse(JSON.stringify(items));
        if (items.length > 0) {
          if (!items[0].photos) items[0].photos = [];
          items[0].photos = [...items[0].photos, ...uploadData.photos];
        }

        // DB'ye photos + items birlikte kaydet
        const saveRes = await fetch(`${API_URL}/repairs/${selectedRepair.id}/photos`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ photos: allPhotos, items: items })
        });
        const saveData = await saveRes.json();
        if (saveData.success) {
          setSelectedRepair(prev => ({ ...prev, photos: allPhotos, items: items }));
          toast.success('Fotoğraf eklendi!');
        } else {
          toast.error('Fotoğraf kaydedilemedi!');
        }
      }
    } catch (error) {
      toast.error('Fotoğraf yüklenemedi!');
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  // ESC tuşu ile modal kapatma
  useEscapeClose([
    [!!viewingPhoto, () => setViewingPhoto(null)],
    [showDetailModal, () => setShowDetailModal(false)],
    [showReceiveModal, () => setShowReceiveModal(false)],
    [showDispatchModal, () => setShowDispatchModal(false)],
    [showProfileMenu, () => setShowProfileMenu(false)],
  ]);

  useEffect(() => {
    fetchRepairs();
    fetchStores();
    const interval = setInterval(fetchRepairs, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchStores = async () => {
    try {
      const response = await fetch(`${API_URL}/stores`);
      const data = await response.json();
      if (Array.isArray(data)) {
        setStores(data);
      }
    } catch (error) {
      console.error('Mağazalar yüklenemedi:', error);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchRepairs = async () => {
    setLoading(true);
    try {
      // Kullanıcının bağlı olduğu tamirat noktasını bul
      let userRepairPointId = null;
      try {
        const rpRes = await fetch(`${API_URL}/repair-points`);
        const rpData = await rpRes.json();
        if (Array.isArray(rpData)) {
          const userPoint = rpData.find(rp => rp.user_id === user?.id && rp.has_app_access === 1);
          if (userPoint) userRepairPointId = userPoint.id;
        }
      } catch (e) {}

      const response = await fetch(`${API_URL}/repairs`);
      const data = await response.json();
      if (Array.isArray(data)) {
        // Sadece atölye durumundaki ve uygulama erişimli tamirat noktasına gönderilmiş tamiratları göster
        const workshopRepairs = data.filter(r => {
          const isWorkshopStatus = r.status === 'sent_to_workshop' || r.status === 'at_workshop' || r.status === 'in_progress' || r.status === 'completed';
          if (!isWorkshopStatus) return false;
          // Uygulama erişimi olmayan noktaya gönderilmişse gösterme
          if (r.repair_point_has_app_access === 0 || r.repair_point_has_app_access === false) return false;
          // Kullanıcının bağlı olduğu tamirat noktası varsa sadece o noktanınkileri göster
          if (userRepairPointId && r.repair_point_id !== userRepairPointId) return false;
          return true;
        });
        setRepairs(workshopRepairs);
      }
    } catch (error) {
      console.error('Tamiratlar yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateRepairStatus = async (repairId, newStatus, repairNotDone = false) => {
    try {
      const response = await fetch(`${API_URL}/repairs/${repairId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: newStatus,
          notes: workshopNote,
          workshop_note: workshopNote,
          repair_not_done: repairNotDone
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Durum güncellendi!');
        fetchRepairs();
        setShowDetailModal(false);
        setWorkshopNote('');
      } else {
        toast.error(data.error || 'Hata oluştu!');
      }
    } catch (error) {
      toast.error('Sunucu hatası!');
    }
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      sent_to_workshop: { label: 'Yolda', color: '#8b5cf6', icon: <Truck size={14} /> },
      at_workshop: { label: 'Atölyede', color: '#FF9800', icon: <Package size={14} /> },
      in_progress: { label: 'İşleniyor', color: '#2196F3', icon: <Wrench size={14} /> },
      completed: { label: 'Tamamlandı', color: '#4CAF50', icon: <CheckCircle size={14} /> },
      returning_to_store: { label: 'Mağazaya Yolda', color: '#14b8a6', icon: <Send size={14} /> }
    };
    return statusMap[status] || { label: status, color: '#9E9E9E', icon: <HelpCircle size={14} /> };
  };

  // Mağaza adını bul
  const getStoreName = (storeId) => {
    if (!storeId) return 'Mağaza belirtilmemiş';
    const store = stores.find(s => s.id === storeId);
    return store ? store.name : 'Bilinmeyen Mağaza';
  };

  // Çıkış yap (mağazaya gönder)
  const getDispatchNote = (repairId) => (dispatchNotes[repairId] || '').trim();
  const setDispatchNote = (repairId, value) => {
    setDispatchNotes(prev => ({ ...prev, [repairId]: value }));
  };

  const dispatchToStore = async () => {
    if (!dispatchRepair) return;
    const note = getDispatchNote(dispatchRepair.id);
    if (!note) {
      toast.warning('Çıkış için not girmek zorunludur!');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/repairs/${dispatchRepair.id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'returning_to_store', workshop_note: note })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`Ürün ${getStoreName(dispatchRepair.store_id)} mağazasına gönderildi!`);
        setShowDispatchModal(false);
        setDispatchNote(dispatchRepair.id, '');
        setDispatchRepair(null);
        fetchRepairs();
      } else {
        toast.error(data.error || 'Hata oluştu!');
      }
    } catch (error) {
      toast.error('Sunucu hatası!');
    }
  };

  // Atölyeye gönderilen ürünleri filtrele
  const pendingReceiveRepairs = repairs.filter(r => r.status === 'sent_to_workshop');
  // Çıkışı yapılabilecek ürünler: atölyeye ulaşmış her ürün (Atölyede / İşleniyor / Tamamlandı).
  // Tamamlananlar önce listelenir.
  const dispatchableRepairs = repairs
    .filter(r => r.status === 'at_workshop' || r.status === 'in_progress' || r.status === 'completed')
    .sort((a, b) => (a.status === 'completed' ? 0 : 1) - (b.status === 'completed' ? 0 : 1));
  const scanList = scanMode === 'dispatch' ? dispatchableRepairs : pendingReceiveRepairs;
  // Çıkışta notu girilmemiş seçili ürün sayısı (hepsi dolmadan çıkış yapılamaz)
  const missingNoteCount = scanMode === 'dispatch'
    ? selectedForReceive.filter(id => !getDispatchNote(id)).length
    : 0;

  const openScanModal = (mode) => {
    setScanMode(mode);
    setSelectedForReceive([]);
    setBarcodeInput('');
    setShowReceiveModal(true);
  };

  // Okutulan barkodu aktif listede bul ve seçime ekle (kamera + manuel giriş ortak)
  const handleScannedCode = (rawCode) => {
    const code = String(rawCode || '').trim();
    if (!code) return;
    const foundRepair = scanList.find(r => normalizeSearch(r.repair_code) === normalizeSearch(code));
    if (foundRepair) {
      if (selectedForReceive.includes(foundRepair.id)) {
        toast.info('Bu ürün zaten seçili!');
      } else {
        setSelectedForReceive(prev => [...prev, foundRepair.id]);
        toast.success(`Ürün eklendi: ${foundRepair.customer_name}`);
      }
      return;
    }
    // Diğer listede mi? Personeli doğru butona yönlendir.
    const otherList = scanMode === 'dispatch' ? pendingReceiveRepairs : dispatchableRepairs;
    const other = otherList.find(r => normalizeSearch(r.repair_code) === normalizeSearch(code));
    if (other) {
      toast.error(scanMode === 'dispatch'
        ? 'Bu ürün henüz teslim alınmamış (Yolda). Önce "Ürün Teslim Al" yapın.'
        : 'Bu ürün zaten atölyede. Çıkış için "Ürün Çıkışı Yap" kullanın.');
    } else {
      toast.error('Barkod bulunamadı!');
    }
  };

  // Kamera ile barkod okunduğunda
  // Kamera callback'i başlatıldığı andaki closure'ı tutar; güncel listeyi ref üzerinden oku.
  const handleScannedCodeRef = useRef(handleScannedCode);
  handleScannedCodeRef.current = handleScannedCode;
  const onBarcodeScanned = useCallback((decodedText) => {
    handleScannedCodeRef.current(decodedText);
  }, []);

  // Kamerayı başlat
  const startCamera = useCallback(async () => {
    if (html5QrCodeRef.current || !scannerContainerRef.current) return;

    try {
      const html5QrCode = new Html5Qrcode("barcode-scanner");
      html5QrCodeRef.current = html5QrCode;

      await html5QrCode.start(
        { facingMode: "environment" }, // Arka kamera (tablet için ideal)
        {
          fps: 10,
          qrbox: { width: 250, height: 150 },
          aspectRatio: 1.5
        },
        (decodedText) => {
          onBarcodeScanned(decodedText);
        },
        () => {} // Hata callback'i (sessiz)
      );

      setIsCameraActive(true);
    } catch (err) {
      console.error('Kamera başlatılamadı:', err);
      toast.error('Kamera erişimi sağlanamadı. Lütfen izin verin.');
      setIsCameraMode(false);
    }
  }, [onBarcodeScanned]);

  // Kamerayı durdur
  const stopCamera = useCallback(async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
      } catch (err) {
        console.error('Kamera durdurulurken hata:', err);
      }
      html5QrCodeRef.current = null;
      setIsCameraActive(false);
    }
  }, []);

  // Modal kapandığında kamerayı durdur
  useEffect(() => {
    if (!showReceiveModal) {
      stopCamera();
    }
  }, [showReceiveModal, stopCamera]);

  // Kamera modu aktif olduğunda kamerayı başlat
  useEffect(() => {
    if (showReceiveModal && isCameraMode) {
      // DOM'un hazır olması için küçük bir gecikme
      const timer = setTimeout(() => {
        startCamera();
      }, 300);
      return () => clearTimeout(timer);
    } else {
      stopCamera();
    }
  }, [showReceiveModal, isCameraMode, startCamera, stopCamera]);

  // Component unmount olduğunda temizle
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  // Barkod okutulduğunda
  const handleBarcodeSubmit = (e) => {
    e.preventDefault();
    handleScannedCode(barcodeInput);
    setBarcodeInput('');
    barcodeInputRef.current?.focus();
  };

  // Checkbox ile seçim
  const toggleReceiveSelection = (repairId) => {
    setSelectedForReceive(prev =>
      prev.includes(repairId)
        ? prev.filter(id => id !== repairId)
        : [...prev, repairId]
    );
  };

  // Tümünü seç/kaldır
  const toggleSelectAll = () => {
    if (selectedForReceive.length === scanList.length) {
      setSelectedForReceive([]);
    } else {
      setSelectedForReceive(scanList.map(r => r.id));
    }
  };

  // Seçili ürünleri teslim al (receive) veya mağazaya çıkışını yap (dispatch)
  const receiveSelectedProducts = async () => {
    if (selectedForReceive.length === 0) {
      toast.warning('Lütfen en az bir ürün seçin!');
      return;
    }
    if (scanSubmitting) return;
    const isDispatch = scanMode === 'dispatch';
    if (isDispatch && missingNoteCount > 0) {
      toast.warning(`${missingNoteCount} ürün için not girilmedi! Her ürün için not zorunludur.`);
      return;
    }
    const targetStatus = isDispatch ? 'returning_to_store' : 'at_workshop';

    setScanSubmitting(true);
    try {
      let successCount = 0;
      for (const repairId of selectedForReceive) {
        try {
          const response = await fetch(`${API_URL}/repairs/${repairId}/status`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(isDispatch
              ? { status: targetStatus, workshop_note: getDispatchNote(repairId) }
              : { status: targetStatus })
          });
          const data = await response.json();
          if (data.success) {
            successCount++;
            if (isDispatch) setDispatchNote(repairId, '');
          }
        } catch (e) {
          // tek ürün hatası diğerlerini durdurmasın
        }
      }
      const failCount = selectedForReceive.length - successCount;
      if (successCount > 0) {
        toast.success(isDispatch ? `${successCount} ürünün mağazaya çıkışı yapıldı!` : `${successCount} ürün teslim alındı!`);
      }
      if (failCount > 0) {
        toast.error(`${failCount} ürün işlenemedi, tekrar deneyin.`);
      }
      setShowReceiveModal(false);
      setSelectedForReceive([]);
      fetchRepairs();
    } catch (error) {
      toast.error('Hata oluştu!');
    } finally {
      setScanSubmitting(false);
    }
  };

  // Modal açıldığında barkod inputuna focus
  useEffect(() => {
    if (showReceiveModal && barcodeInputRef.current) {
      setTimeout(() => barcodeInputRef.current?.focus(), 100);
    }
  }, [showReceiveModal]);

  const stats = {
    pending: repairs.filter(r => r.status === 'sent_to_workshop').length,
    atWorkshop: repairs.filter(r => r.status === 'at_workshop').length,
    inProgress: repairs.filter(r => r.status === 'in_progress').length,
    completed: repairs.filter(r => r.status === 'completed').length,
    total: repairs.length
  };

  // Mağaza listesini repairs'den çıkar
  const storeList = [...new Map(repairs.filter(r => r.store_id).map(r => [r.store_id, { id: r.store_id, name: r.store_name || `Mağaza ${r.store_id}` }])).values()];

  // Seçili mağaza listede yoksa (veri yenilendi, buton gizlendi vb.) filtre takılı kalmasın
  const activeStoreFilter = storeList.some(st => String(st.id) === filterStoreId) ? filterStoreId : 'all';

  // Sekme değişince mağaza filtresini sıfırla
  const changeTab = (tab) => {
    setActiveTab(tab);
    setFilterStoreId('all');
  };

  const filteredRepairs = repairs.filter(r => {
    if (activeStoreFilter !== 'all' && String(r.store_id) !== activeStoreFilter) return false;
    if (activeTab === 'pending' && r.status !== 'sent_to_workshop') return false;
    if (activeTab === 'atWorkshop' && r.status !== 'at_workshop') return false;
    if (activeTab === 'inProgress' && r.status !== 'in_progress') return false;
    if (activeTab === 'completed' && r.status !== 'completed') return false;
    if (searchTerm.trim()) {
      const term = normalizeSearch(searchTerm);
      const items = (() => { try { return r.items ? (typeof r.items === 'string' ? JSON.parse(r.items) : r.items) : []; } catch { return []; } })();
      const itemsText = items.map(i => `${i.item_description || ''} ${i.problem_description || ''}`).join(' ');
      const haystack = normalizeSearch([r.customer_name, r.repair_code, r.item_description, itemsText, r.notes].join(' '));
      if (!haystack.includes(term)) return false;
    }
    return true;
  });

  return (
    <div className="workshop">
      {/* Header */}
      <header className="workshop-header">
        <div className="header-content">
          <h1>Atölye Paneli</h1>
          <p>Tamirat ve onarım işlemleri</p>
        </div>
        <div className="workshop-header-actions">
        <button
          className="receive-products-btn"
          onClick={() => openScanModal('receive')}
        >
          <Truck size={20} /> Ürün Teslim Al
          {pendingReceiveRepairs.length > 0 && (
            <span className="pending-badge">{pendingReceiveRepairs.length}</span>
          )}
        </button>
        <button
          className="receive-products-btn workshop-dispatch-scan-btn"
          onClick={() => openScanModal('dispatch')}
        >
          <Send size={20} /> Ürün Çıkışı Yap
          {stats.completed > 0 && (
            <span className="pending-badge">{stats.completed}</span>
          )}
        </button>
        </div>
        <div className="header-logo">
          <img
            src={(user?.store_id === 1 || selectedStore?.id === 1) ? "/Siyah - Beyaz - 1.png" : "/logo.png"}
            alt="Akik"
          />
        </div>
        <div className="header-user" ref={profileMenuRef}>
          <div
            className="user-avatar"
            onClick={() => setShowProfileMenu(!showProfileMenu)}
          >
            {user?.name?.charAt(0).toUpperCase() || 'A'}
          </div>
          {showProfileMenu && (
            <div className="profile-dropdown">
              <div className="profile-info">
                <span className="profile-name">{user?.name}</span>
                <span className="profile-email">{user?.email}</span>
                <span className="profile-role">Atölye</span>
              </div>
              <div className="profile-divider"></div>
              <button className="profile-menu-item logout" onClick={onLogout}>
                <LogOut size={16} /> Çıkış Yap
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Tabs */}
      <div className="workshop-tabs">
        <button
          className={`tab-btn ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => changeTab('pending')}
        >
          <Truck size={18} /> Yolda ({stats.pending})
        </button>
        <button
          className={`tab-btn ${activeTab === 'atWorkshop' ? 'active' : ''}`}
          onClick={() => changeTab('atWorkshop')}
        >
          <Package size={18} /> Atölyede ({stats.atWorkshop})
        </button>
        <button
          className={`tab-btn ${activeTab === 'inProgress' ? 'active' : ''}`}
          onClick={() => changeTab('inProgress')}
        >
          <Wrench size={18} /> İşlenenler ({stats.inProgress})
        </button>
        <button
          className={`tab-btn ${activeTab === 'completed' ? 'active' : ''}`}
          onClick={() => changeTab('completed')}
        >
          <CheckCircle size={18} /> Tamamlananlar ({stats.completed})
        </button>
        <button
          className={`tab-btn ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => changeTab('all')}
        >
          <ClipboardList size={18} /> Tümünü Gör ({stats.total})
        </button>
      </div>

      {/* Content */}
      <div className="workshop-content">
        {/* Arama */}
        <div style={{ position: 'relative', marginBottom: '12px' }}>
          <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <input
            type="text"
            placeholder="Müşteri adı, barkod, ürün ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: '100%', padding: '8px 12px 8px 36px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.85rem', background: '#fff', boxSizing: 'border-box' }}
          />
        </div>

        {/* Mağaza Filtresi */}
        {storeList.length > 1 && (
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
            <button
              onClick={() => setFilterStoreId('all')}
              style={{
                padding: '6px 14px', borderRadius: '8px', border: '1.5px solid', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600,
                borderColor: activeStoreFilter === 'all' ? '#14b8a6' : '#e2e8f0',
                background: activeStoreFilter === 'all' ? '#14b8a6' : '#fff',
                color: activeStoreFilter === 'all' ? '#fff' : '#64748b'
              }}
            >
              <Store size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} /> Tümü
            </button>
            {storeList.map(s => (
              <button
                key={s.id}
                onClick={() => setFilterStoreId(String(s.id))}
                style={{
                  padding: '6px 14px', borderRadius: '8px', border: '1.5px solid', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600,
                  borderColor: activeStoreFilter === String(s.id) ? '#14b8a6' : '#e2e8f0',
                  background: activeStoreFilter === String(s.id) ? '#14b8a6' : '#fff',
                  color: activeStoreFilter === String(s.id) ? '#fff' : '#64748b'
                }}
              >
                {s.name}
              </button>
            ))}
          </div>
        )}
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Yükleniyor...</p>
          </div>
        ) : filteredRepairs.length === 0 ? (
          <div className="empty-state">
            <Inbox size={64} strokeWidth={1} className="empty-icon" />
            <p>Bekleyen tamirat yok</p>
          </div>
        ) : (
          <div className="repairs-grid">
            {filteredRepairs.map(repair => (
              <div key={repair.id} className="repair-card" onClick={() => { setSelectedRepair(repair); setShowDetailModal(true); }} style={{ cursor: 'pointer' }}>
                <div className="repair-header">
                  <span className="repair-code">{repair.repair_code}</span>
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    {repair.repair_not_done === 1 && (
                      <span style={{ padding: '3px 6px', borderRadius: '12px', fontSize: '0.65rem', fontWeight: 700, background: '#fef2f2', color: '#dc2626', border: '1px solid #f87171' }}>YAPILMADI</span>
                    )}
                    <span
                      className="status-badge"
                      style={{ background: getStatusInfo(repair.status).color }}
                    >
                      {getStatusInfo(repair.status).icon} {getStatusInfo(repair.status).label}
                    </span>
                  </div>
                </div>
                <div className="repair-body">
                  <p className="repair-customer">
                    <span className="label">Müşteri:</span> {repair.customer_name}
                  </p>
                  {repair.store_name && (
                    <p style={{ margin: '2px 0', fontSize: '0.8rem', color: '#0d9488' }}>
                      <span className="label"><Store size={12} style={{ marginRight: '2px', verticalAlign: 'middle' }} />Mağaza:</span> {repair.store_name}
                    </p>
                  )}
                  {(() => {
                    let items = [];
                    try { items = repair.items ? (typeof repair.items === 'string' ? JSON.parse(repair.items) : repair.items) : []; } catch (e) { items = []; }
                    if (!items || items.length === 0) items = [{ item_description: repair.item_description, problem_description: repair.problem_description }];
                    return items.filter(i => i.item_description?.trim()).map((item, idx) => (
                      <p key={idx} className="repair-problem" style={{ margin: '2px 0' }}>
                        <span className="label">{items.length > 1 ? `${idx + 1}.` : 'Ürün:'}</span> {item.item_description}{item.problem_description ? ` - ${item.problem_description}` : ''}
                      </p>
                    ));
                  })()}
                  {repair.notes && (
                    <p style={{ margin: '4px 0 2px', fontSize: '0.78rem', color: '#64748b', fontStyle: 'italic' }}>
                      <span className="label">Not:</span> {repair.notes}
                    </p>
                  )}
                  <p className="repair-date">
                    <span className="label">Tarih:</span> {new Date(repair.created_at).toLocaleDateString('tr-TR')}
                    {repair.deadline && <span style={{ marginLeft: '8px', color: new Date(repair.deadline) < new Date() ? '#dc2626' : '#f59e0b', fontWeight: 600, fontSize: '0.75rem' }}>Son: {new Date(repair.deadline).toLocaleDateString('tr-TR')}</span>}
                  </p>
                </div>
                <div onClick={(e) => e.stopPropagation()}>
                  {repair.status === 'sent_to_workshop' && (
                    <button
                      className="card-fab-btn start"
                      onClick={() => updateRepairStatus(repair.id, 'at_workshop')}
                      title="Teslim Al"
                    >
                      <Check size={18} />
                    </button>
                  )}
                  {repair.status === 'at_workshop' && (
                    <button
                      className="card-fab-btn start"
                      onClick={() => updateRepairStatus(repair.id, 'in_progress')}
                      title="Tamirata Başla"
                    >
                      <Play size={18} />
                    </button>
                  )}
                  {repair.status === 'in_progress' && (
                    <>
                      <button
                        className="card-fab-btn complete"
                        onClick={() => updateRepairStatus(repair.id, 'completed')}
                        title="Tamamla"
                      >
                        <CheckCircle size={18} />
                      </button>
                      <button
                        className="card-fab-btn not-done"
                        onClick={async () => {
                          await updateRepairStatus(repair.id, 'completed', true);
                          setDispatchRepair({...repair, repair_not_done: 1});
                          setShowDispatchModal(true);
                        }}
                        title="Tamiratsız Gönder"
                      >
                        <X size={18} />
                      </button>
                    </>
                  )}
                  {repair.status === 'completed' && (
                    <button
                      className="card-fab-btn dispatch"
                      onClick={() => {
                        setDispatchRepair(repair);
                        setShowDispatchModal(true);
                      }}
                      title="Çıkış Yap"
                    >
                      <Send size={18} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedRepair && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Tamirat Detayı - {selectedRepair.repair_code}</h2>
              <button className="close-btn" onClick={() => setShowDetailModal(false)}><X size={18} /></button>
            </div>
            <div className="modal-body" style={{ padding: '14px 18px' }}>
              {/* Müşteri + Durum + Ücret */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '12px' }}>
                <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '8px 10px', border: '1px solid #e2e8f0' }}>
                  <span style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>Müşteri</span>
                  <div style={{ fontWeight: 600, color: '#1e293b', marginTop: '2px', fontSize: '0.9rem' }}>{selectedRepair.customer_name}</div>
                  {selectedRepair.customer_phone && <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{selectedRepair.customer_phone}</div>}
                </div>
                <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '8px 10px', border: '1px solid #e2e8f0' }}>
                  <span style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>Durum</span>
                  <div style={{ marginTop: '2px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    <span className="status-badge" style={{ background: getStatusInfo(selectedRepair.status).color }}>{getStatusInfo(selectedRepair.status).label}</span>
                    {selectedRepair.repair_not_done === 1 && (
                      <span style={{ padding: '3px 6px', borderRadius: '12px', fontSize: '0.65rem', fontWeight: 700, background: '#fef2f2', color: '#dc2626', border: '1px solid #f87171' }}>YAPILMADI</span>
                    )}
                  </div>
                </div>
                <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '8px 10px', border: '1px solid #e2e8f0' }}>
                  <span style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>Ücret</span>
                  <div style={{ fontWeight: 600, marginTop: '2px', fontSize: '0.9rem', color: selectedRepair.price_type === 'free' ? '#10b981' : selectedRepair.price_type === 'pending' ? '#f59e0b' : '#1e293b' }}>
                    {selectedRepair.price_type === 'free' ? 'Ücretsiz' : selectedRepair.price_type === 'pending' ? 'Hesaplanacak' : Number(selectedRepair.estimated_price || 0) > 0 ? `₺${Number(selectedRepair.estimated_price).toLocaleString('tr-TR')}` : '-'}
                  </div>
                </div>
              </div>

              {/* Ürünler */}
              <div style={{ marginBottom: '12px' }}>
                <span style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>Ürünler ve Yapılacak İşlemler</span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' }}>
                  {(() => {
                    let items = [];
                    try { items = selectedRepair.items ? (typeof selectedRepair.items === 'string' ? JSON.parse(selectedRepair.items) : selectedRepair.items) : []; } catch (e) { items = []; }
                    if (!items || items.length === 0) items = [{ item_description: selectedRepair.item_description, problem_description: selectedRepair.problem_description, photos: selectedRepair.photos ? (typeof selectedRepair.photos === 'string' ? JSON.parse(selectedRepair.photos) : selectedRepair.photos) : [] }];
                    return items.filter(i => i.item_description?.trim()).map((item, idx) => (
                      <div key={idx} style={{ background: '#f8fafc', borderRadius: '6px', padding: '8px 10px', border: '1px solid #e2e8f0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ color: '#94a3b8', fontWeight: 700, fontSize: '0.8rem' }}>{idx + 1}</span>
                          <span style={{ flex: 1, fontWeight: 500, color: '#1e293b', fontSize: '0.85rem' }}>{item.item_description}{item.problem_description ? ` - ${item.problem_description}` : ''}</span>
                        </div>
                        {item.photos && item.photos.length > 0 && (
                          <div style={{ display: 'flex', gap: '6px', marginTop: '6px', marginLeft: '18px' }}>
                            {item.photos.map((photo, pIdx) => (
                              <img key={pIdx} src={`${API_URL.replace('/api', '')}${photo}`} alt={`Foto ${pIdx + 1}`} onClick={() => setViewingPhoto(`${API_URL.replace('/api', '')}${photo}`)} style={{ width: '48px', height: '48px', borderRadius: '6px', objectFit: 'cover', border: '1px solid #e2e8f0', cursor: 'pointer' }} />
                            ))}
                          </div>
                        )}
                      </div>
                    ));
                  })()}
                </div>
              </div>

              {/* Tarihler */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '12px' }}>
                <div style={{ background: '#f8fafc', borderRadius: '6px', padding: '6px 10px', border: '1px solid #e2e8f0' }}>
                  <span style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 600 }}>Kayıt</span>
                  <div style={{ fontSize: '0.8rem', fontWeight: 500 }}>{new Date(selectedRepair.created_at).toLocaleDateString('tr-TR')}</div>
                </div>
                <div style={{ background: '#f8fafc', borderRadius: '6px', padding: '6px 10px', border: '1px solid #e2e8f0' }}>
                  <span style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 600 }}>Tahmini Teslim</span>
                  <div style={{ fontSize: '0.8rem', fontWeight: 500 }}>{selectedRepair.estimated_delivery ? new Date(selectedRepair.estimated_delivery).toLocaleDateString('tr-TR') : '-'}</div>
                </div>
                <div style={{ background: '#f8fafc', borderRadius: '6px', padding: '6px 10px', border: '1px solid #e2e8f0' }}>
                  <span style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 600 }}>En Geç Teslim</span>
                  <div style={{ fontSize: '0.8rem', fontWeight: selectedRepair.deadline ? 600 : 400, color: selectedRepair.deadline && new Date(selectedRepair.deadline) < new Date() ? '#dc2626' : '#1e293b' }}>{selectedRepair.deadline ? new Date(selectedRepair.deadline).toLocaleDateString('tr-TR') : '-'}</div>
                </div>
              </div>

              {/* Notlar */}
              {selectedRepair.notes && (
                <div style={{ padding: '6px 10px', background: '#fffbeb', borderRadius: '6px', border: '1px solid #fde68a', fontSize: '0.8rem', color: '#92400e', marginBottom: '12px' }}>
                  <span style={{ fontWeight: 600 }}>Not:</span> {selectedRepair.notes}
                </div>
              )}

              {/* Fotoğraf Ekleme */}
              <div style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>Fotoğraflar</span>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <input type="file" id="workshop-camera-input" accept="image/*" capture="environment" onChange={handleWorkshopPhotoUpload} style={{ display: 'none' }} />
                    <input type="file" id="workshop-gallery-input" accept="image/*" multiple onChange={handleWorkshopPhotoUpload} style={{ display: 'none' }} />
                    <button type="button" onClick={() => document.getElementById('workshop-camera-input')?.click()} disabled={uploadingPhoto} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: '0.8rem', color: '#64748b' }}>
                      <Camera size={14} /> Kamera
                    </button>
                    <button type="button" onClick={() => document.getElementById('workshop-gallery-input')?.click()} disabled={uploadingPhoto} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: '0.8rem', color: '#64748b' }}>
                      <Image size={14} /> Galeri
                    </button>
                  </div>
                </div>
                {uploadingPhoto && <p style={{ fontSize: '0.75rem', color: '#64748b' }}>Yükleniyor...</p>}
                {(() => {
                  let allPhotos = [];
                  try {
                    let items = selectedRepair.items ? (typeof selectedRepair.items === 'string' ? JSON.parse(selectedRepair.items) : selectedRepair.items) : [];
                    items.forEach(item => { if (item.photos) allPhotos.push(...item.photos); });
                    if (allPhotos.length === 0) {
                      const photos = selectedRepair.photos ? (typeof selectedRepair.photos === 'string' ? JSON.parse(selectedRepair.photos) : selectedRepair.photos) : [];
                      allPhotos = photos;
                    }
                  } catch (e) {}
                  return allPhotos.length > 0 ? (
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {allPhotos.map((photo, pIdx) => (
                        <img key={pIdx} src={`${API_URL.replace('/api', '')}${photo}`} alt={`Foto ${pIdx + 1}`} onClick={() => setViewingPhoto(`${API_URL.replace('/api', '')}${photo}`)} style={{ width: '56px', height: '56px', borderRadius: '6px', objectFit: 'cover', border: '1px solid #e2e8f0', cursor: 'pointer' }} />
                      ))}
                    </div>
                  ) : <p style={{ fontSize: '0.8rem', color: '#94a3b8' }}>Henüz fotoğraf yok</p>;
                })()}
              </div>

              <div className="note-section">
                <label>Atölye Notu</label>
                <textarea
                  value={workshopNote}
                  onChange={e => setWorkshopNote(e.target.value)}
                  placeholder="Tamirat hakkında not ekleyin..."
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowDetailModal(false)}>Kapat</button>
              {selectedRepair.status === 'sent_to_workshop' && (
                <button
                  className="btn-start"
                  onClick={() => updateRepairStatus(selectedRepair.id, 'at_workshop')}
                >
                  <Check size={16} /> Teslim Al
                </button>
              )}
              {selectedRepair.status === 'at_workshop' && (
                <button
                  className="btn-start"
                  onClick={() => updateRepairStatus(selectedRepair.id, 'in_progress')}
                >
                  <Play size={16} /> İşleme Al
                </button>
              )}
              {selectedRepair.status === 'in_progress' && (
                <>
                  <button
                    className="btn-complete"
                    onClick={() => updateRepairStatus(selectedRepair.id, 'completed')}
                  >
                    <CheckCircle size={16} /> Tamiratı Tamamla
                  </button>
                  <button
                    style={{ padding: '12px 24px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                    onClick={async () => {
                      await updateRepairStatus(selectedRepair.id, 'completed', true);
                      setDispatchRepair({...selectedRepair, repair_not_done: 1});
                      setShowDispatchModal(true);
                    }}
                  >
                    <X size={16} /> Tamiratsız Gönder
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Ürün Teslim Al Modal */}
      {showReceiveModal && (
        <div className="modal-overlay">
          <div className="receive-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{scanMode === 'dispatch' ? <><Send size={22} /> Ürün Çıkışı Yap</> : <><Truck size={22} /> Ürün Teslim Al</>}</h2>
              <button className="close-btn" onClick={() => setShowReceiveModal(false)}><X size={18} /></button>
            </div>
            <div className="receive-modal-body">
              {/* Sol Taraf - Barkod Okutma */}
              <div className="barcode-section">
                <h3><ScanBarcode size={20} /> Barkod Okut</h3>

                {/* Manuel Giriş */}
                <form onSubmit={handleBarcodeSubmit} className="manual-input-form">
                  <input
                    ref={barcodeInputRef}
                    type="text"
                    value={barcodeInput}
                    onChange={e => setBarcodeInput(e.target.value)}
                    placeholder="Barkodu okutun veya yazın..."
                    className="barcode-input"
                    autoFocus
                  />
                  <button type="submit" className="scan-btn">
                    <Check size={18} /> Ekle
                  </button>
                </form>

                {/* Kamera ile Okut Butonu */}
                <button
                  type="button"
                  className={`camera-toggle-btn ${isCameraMode ? 'active' : ''}`}
                  onClick={() => setIsCameraMode(!isCameraMode)}
                >
                  <Camera size={18} />
                  {isCameraMode ? 'Kamerayı Kapat' : 'Kamera ile Okut'}
                </button>

                {/* Kamera Görüntüsü */}
                {isCameraMode && (
                  <div className="camera-scanner-container" ref={scannerContainerRef}>
                    <div id="barcode-scanner"></div>
                    {!isCameraActive && (
                      <div className="camera-loading">
                        <div className="spinner"></div>
                        <p>Kamera başlatılıyor...</p>
                      </div>
                    )}
                    <div className="camera-instructions">
                      <p>Barkodu kamera görüş alanına getirin</p>
                    </div>
                  </div>
                )}

                <div className="barcode-info">
                  <p>{scanMode === 'dispatch'
                    ? 'Fiş barkodunu okutun; ürün listede aramadan çıkışa eklenir'
                    : 'Fiş üzerindeki barkodu okutarak ürünü otomatik seçin'}</p>
                </div>
                <div className="selected-count">
                  <strong>{selectedForReceive.length}</strong> ürün seçildi
                </div>
              </div>

              {/* Sağ Taraf - Ürün Listesi */}
              <div className="products-section">
                <div className="products-header">
                  <h3><Package size={20} /> {scanMode === 'dispatch' ? 'Atölyedeki Ürünler' : 'Bekleyen Ürünler'} ({scanList.length})</h3>
                  {scanList.length > 0 && (
                    <button className="select-all-btn" onClick={toggleSelectAll}>
                      {selectedForReceive.length === scanList.length ? 'Seçimi Kaldır' : 'Tümünü Seç'}
                    </button>
                  )}
                </div>
                <div className="products-list">
                  {scanList.length === 0 ? (
                    <div className="no-products">
                      <Inbox size={48} />
                      <p>{scanMode === 'dispatch' ? 'Atölyede ürün yok' : 'Bekleyen ürün yok'}</p>
                    </div>
                  ) : (
                    scanList.map(repair => {
                      const isSelected = selectedForReceive.includes(repair.id);
                      const statusInfo = getStatusInfo(repair.status);
                      return (
                        <div
                          key={repair.id}
                          className={`product-item ${isSelected ? 'selected' : ''}`}
                          onClick={() => toggleReceiveSelection(repair.id)}
                        >
                          <div className="product-checkbox">
                            {isSelected && <Check size={18} />}
                          </div>
                          <div className="product-info">
                            <div className="product-code">{repair.repair_code}</div>
                            <div className="product-customer">{repair.customer_name}</div>
                            {scanMode === 'dispatch' && (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '2px', fontSize: '0.75rem' }}>
                                <span style={{ color: statusInfo.color, fontWeight: 700 }}>{statusInfo.label}</span>
                                <span style={{ color: '#64748b' }}>→ {getStoreName(repair.store_id)}</span>
                              </div>
                            )}
                            <div className="product-desc">
                              {repair.item_description || repair.problem_description || '-'}
                            </div>
                            {scanMode === 'dispatch' && isSelected && (
                              <textarea
                                className={`workshop-dispatch-note ${getDispatchNote(repair.id) ? '' : 'missing'}`}
                                placeholder="Çıkış notu (zorunlu) — yapılan işlem vb."
                                rows={2}
                                value={dispatchNotes[repair.id] || ''}
                                onClick={e => e.stopPropagation()}
                                onChange={e => setDispatchNote(repair.id, e.target.value)}
                              />
                            )}
                          </div>
                          <div className="product-date">
                            {new Date(repair.created_at).toLocaleDateString('tr-TR')}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowReceiveModal(false)}>İptal</button>
              <button
                className="btn-receive"
                onClick={receiveSelectedProducts}
                disabled={selectedForReceive.length === 0 || scanSubmitting || missingNoteCount > 0}
              >
                {scanMode === 'dispatch'
                  ? <><Send size={18} /> {scanSubmitting ? 'Gönderiliyor...' : missingNoteCount > 0 ? `${missingNoteCount} Ürüne Not Girin` : `${selectedForReceive.length} Ürünün Çıkışını Yap`}</>
                  : <><Check size={18} /> {scanSubmitting ? 'Kaydediliyor...' : `${selectedForReceive.length} Ürünü Teslim Al`}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Çıkış Yap Modal */}
      {showDispatchModal && dispatchRepair && (
        <div className="modal-overlay">
          <div className="dispatch-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Send size={22} /> Ürün Çıkışı</h2>
              <button className="close-btn" onClick={() => setShowDispatchModal(false)}><X size={18} /></button>
            </div>
            <div className="dispatch-modal-body">
              <div className="dispatch-info-card">
                <div className="dispatch-icon">
                  <Store size={48} />
                </div>
                <div className="dispatch-details">
                  <h3>Gönderilecek Mağaza</h3>
                  <p className="store-name">{getStoreName(dispatchRepair.store_id)}</p>
                </div>
              </div>

              <div className="dispatch-product-info">
                <div className="info-row">
                  <span className="label">Fiş Kodu:</span>
                  <span className="value code">{dispatchRepair.repair_code}</span>
                </div>
                <div className="info-row">
                  <span className="label">Müşteri:</span>
                  <span className="value">{dispatchRepair.customer_name}</span>
                </div>
                <div className="info-row">
                  <span className="label">Ürün:</span>
                  <span className="value">{dispatchRepair.product_name || dispatchRepair.item_description || '-'}</span>
                </div>
                <div className="info-row">
                  <span className="label">Sorun:</span>
                  <span className="value">{dispatchRepair.problem_description || '-'}</span>
                </div>
              </div>

              {dispatchRepair.repair_not_done === 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', background: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca', marginBottom: '12px' }}>
                  <X size={18} style={{ color: '#dc2626' }} />
                  <p style={{ margin: 0, color: '#dc2626', fontWeight: 600, fontSize: '0.9rem' }}>Bu ürüne tamirat yapılmadı!</p>
                </div>
              )}
              <div className="dispatch-warning">
                <MapPin size={18} />
                <p>Bu ürün <strong>{getStoreName(dispatchRepair.store_id)}</strong> mağazasına gönderilecek ve durum "Mağazaya Yolda" olarak güncellenecek.</p>
              </div>
              <label className="workshop-dispatch-note-label">Çıkış Notu <span>*</span></label>
              <textarea
                className={`workshop-dispatch-note ${getDispatchNote(dispatchRepair.id) ? '' : 'missing'}`}
                placeholder="Yapılan işlemi / ürünle ilgili notu yazın (zorunlu)"
                rows={3}
                autoFocus
                value={dispatchNotes[dispatchRepair.id] || ''}
                onChange={e => setDispatchNote(dispatchRepair.id, e.target.value)}
              />
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowDispatchModal(false)}>İptal</button>
              <button className="btn-dispatch" onClick={dispatchToStore} disabled={!getDispatchNote(dispatchRepair.id)}>
                <Send size={18} /> Çıkışını Yap
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fotoğraf Büyütme Modal */}
      {viewingPhoto && (
        <div className="photo-modal-overlay" onClick={() => setViewingPhoto(null)}>
          <button className="photo-modal-close" onClick={() => setViewingPhoto(null)}>✕</button>
          <div className="photo-modal-content" onClick={(e) => e.stopPropagation()}>
            <img src={viewingPhoto} alt="Büyük görünüm" />
          </div>
        </div>
      )}
    </div>
  );
}

export default Workshop;
